import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useHashAnchor } from '../hooks/useHashAnchor';
import { GUIDE_GROUPS, GUIDES, GuideMeta } from './help/guideData';
import { GUIDE_BODIES } from './help/GuideContent';
import { premiumGold } from '../components/canonicalAccents';
import {
  eyebrow,
  sectionTitle,
  sectionLead,
  MarketingLink,
  useGuideTheme,
} from './help/guideBlocks';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const GuideSection: React.FC<{ guide: GuideMeta; group: string; alt?: boolean }> = ({ guide, group, alt }) => {
  const { theme, dsColors, colors, heroPrimary, heroSecondary } = useGuideTheme();
  const Body = GUIDE_BODIES[guide.id];
  const eyebrowColor = group === 'plans' ? premiumGold(theme) : dsColors.treadGray;
  return (
    <section
      id={guide.id}
      className="py-14 md:py-20 scroll-mt-24 border-t"
      style={{ backgroundColor: alt ? colors.surface : colors.background, borderColor: colors.border }}
    >
      <div className="container mx-auto px-4 sm:px-6 help-article">
        <AnimatedSection>
          <p className={eyebrow} style={{ color: eyebrowColor }}>{guide.eyebrow}</p>
          <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>{guide.title}</h2>
          {guide.lead ? (
            <p className={`${sectionLead} mb-8`} style={{ color: heroSecondary, textWrap: 'pretty' }}>{guide.lead}</p>
          ) : (
            <div className="mb-8" />
          )}
          {Body && <Body />}
          {guide.marketing && (
            <p className="mt-10">
              <MarketingLink to={guide.marketing.to} label={guide.marketing.label} />
            </p>
          )}
        </AnimatedSection>
      </div>
    </section>
  );
};

const Help: React.FC = () => {
  const { dsColors, colors, heroPrimary, heroSecondary, cardBorder } = useGuideTheme();
  useHashAnchor();

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Hero — quiet typographic; this is a utility hub, not a marketing page */}
      <section className="pt-20 pb-12 md:pt-28 md:pb-16" style={{ backgroundColor: colors.surface }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <p className={eyebrow} style={{ color: dsColors.primary }}>Help &amp; Guides</p>
          <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-[1.05]" style={{ color: heroPrimary, textWrap: 'balance' }}>
            How to Use RidePulse
          </h1>
          <p className={sectionLead} style={{ color: heroSecondary, textWrap: 'pretty' }}>
            Illustrated, step-by-step guides that follow the app's own journeys, from setting up your account to riding with GroupPulse. New here? Start with <Link to="/getting-started" className="font-semibold hover:underline focus:outline-none focus-visible:ring-2 rounded" style={{ color: dsColors.primary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}>Getting Started</Link>.
          </p>
        </div>
      </section>

      {/* Guide index grouped by app journey */}
      <nav aria-label="Guide index" className="py-10 border-b" style={{ backgroundColor: colors.background, borderColor: cardBorder }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl space-y-6">
          {GUIDE_GROUPS.map((group) => (
            <div key={group.id} className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
              <span className="text-xs font-bold uppercase tracking-widest w-36 shrink-0" style={{ color: heroSecondary }}>
                {group.label}
              </span>
              <ul className="flex flex-wrap gap-2">
                {group.guideIds.map((id) => (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      className="inline-block text-sm font-semibold px-4 py-2 rounded-full transition-colors focus:outline-none focus-visible:ring-2"
                      style={{ backgroundColor: colors.surface, color: heroPrimary, border: `1px solid ${cardBorder}`, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                    >
                      {GUIDES[id].nav}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </nav>

      {GUIDE_GROUPS.map((group, gi) =>
        group.guideIds.map((id, i) => (
          <GuideSection key={id} guide={GUIDES[id]} group={group.id} alt={(gi + i) % 2 === 0} />
        ))
      )}

      <Footer />
    </div>
  );
};

export default Help;
